import React from "react";
import { Waves, Moon, Activity, Heart, Battery, Sunrise } from "lucide-react";
import ApplicationTemplate from "@/components/applications/ApplicationTemplate";

const StressBurnout = () => (
  <ApplicationTemplate
    pageKey="stress-burnout"
    breadcrumbLabel="Stress & Burnout"
    overline="Application · Stress & Burnout"
    headline="Burnout is a physiological adaptation."
    headlineAccent="Rest alone rarely resolves it."
    lead="Chronic stress trains the nervous system into sustained sympathetic activation. Over time, the system’s set-point shifts — and rest, taken from that new default, doesn’t produce recovery. Adaptive Physiology addresses the set-point itself."
    loopHighlight="state"
    challenge={{
      title: "The system adapted to load. Now it can’t leave activation.",
      paragraphs: [
        "Under sustained demand, the autonomic nervous system biases toward sympathetic dominance. Cortisol rhythm flattens, HRV narrows, and parasympathetic access — the branch responsible for recovery — becomes physiologically less available.",
        "The result is a state in which effort feels heavier, sleep feels unrefreshing, and recovery doesn’t recover. The physiology has learned to run this way.",
      ],
    }}
    traditionalGap={{
      title: "Rest advice assumes a system that can access rest.",
      paragraphs: [
        "Vacations, sabbaticals, and self-care practices are valuable — but they operate on top of an unchanged physiological default. A weekend does not retrain an autonomic system that has spent years adapting to load.",
        "Adaptive Physiology is not opposed to rest. It creates the physiological conditions under which rest actually works.",
      ],
    }}
    perspective={{
      title: "Shift the set-point. The recovery will follow.",
      paragraphs: [
        "Burnout responds to structured, precisely dosed inputs — not more rest, and not more discipline. Zone 2 aerobic work, breath-driven vagal loading, and rhythmic sleep architecture are used to widen autonomic capacity and restore parasympathetic access.",
        "The goal is not to prove resilience. It is to give the nervous system the inputs required to change what it defaults to.",
      ],
    }}
    frameworkApplication={[
      {
        title: "Baseline the load and the recovery",
        body: "HRV, resting HR, cortisol pattern (where available), sleep architecture, subjective energy, and current daily demand structure.",
      },
      {
        title: "Prescribe the parasympathetic stimulus",
        body: "Aerobic base work, respiratory pacing, sleep protocol, environmental cues — dosed to shift the system, not to add load.",
      },
      {
        title: "Reinforce the recovery rhythm",
        body: "Repetition establishes the new signal. Daily and weekly cadence guard the intervention from being overwritten by demand.",
      },
      {
        title: "Retrain the autonomic default",
        body: "Parasympathetic access widens. Recovery begins to happen on its own — because the underlying state has changed.",
      },
    ]}
    capacities={[
      { icon: Waves, label: "Parasympathetic access", body: "The physiological ability to actually enter a recovery state." },
      { icon: Heart, label: "Autonomic capacity", body: "A wider range of stress the system can absorb before defaulting." },
      { icon: Moon, label: "Sleep architecture", body: "Deeper, more consolidated recovery during sleep windows." },
      { icon: Activity, label: "Effort tolerance", body: "Sustained output without triggering the crash cycle." },
      { icon: Battery, label: "Energy regulation", body: "Steadier energy across the day, not oscillating extremes." },
      { icon: Sunrise, label: "Recovery velocity", body: "Faster return to baseline after peak demand." },
    ]}
  />
);
export default StressBurnout;
